import { canAccessTier, getEffectiveEpisodeAccess } from "./catalog-access.ts";
import { PATREON_JOIN_URL, joinLabelForTier, safePatreonPostHref, upgradeLabelForTier } from "./patreon-links.ts";
import type { AccessLevel, Episode } from "./types";

export type EpisodeActionContext = "card" | "detail";
export type EpisodePrimaryAction =
  | { type: "navigate"; label: string; href: string; reason: string }
  | { type: "resolve"; label: string; reason: string }
  | { type: "disabled"; label: string; reason: string };

function unlockLabel(episode: Episode) {
  const effective = getEffectiveEpisodeAccess(episode);
  if (effective.isProgressiveCut) return joinLabelForTier("progressive");
  if (effective.isPlatinumEarlyAccess) return joinLabelForTier("platinum_early");
  if (effective.isAllPaidMembers) return joinLabelForTier("all_paid");
  return joinLabelForTier(effective.effectiveTierRequired);
}

function upgradeLabel(episode: Episode) {
  const effective = getEffectiveEpisodeAccess(episode);
  if (effective.isProgressiveCut) return upgradeLabelForTier("progressive");
  if (effective.isPlatinumEarlyAccess) return upgradeLabelForTier("platinum_early");
  if (effective.isAllPaidMembers) return upgradeLabelForTier("all_paid");
  return upgradeLabelForTier(effective.effectiveTierRequired);
}

export function getEpisodePrimaryAction(
  episode: Episode,
  accessLevel: AccessLevel,
  context: EpisodeActionContext
): EpisodePrimaryAction {
  const effective = getEffectiveEpisodeAccess(episode);
  const eligible = canAccessTier(effective.effectiveTierRequired, accessLevel);

  if (context === "card") {
    return {
      type: "navigate",
      label: "Preview Details",
      href: `/episode/${episode.slug}`,
      reason: "Card CTAs always open the preview/detail route."
    };
  }

  if (effective.originalTierRequired === "free") {
    const postHref = safePatreonPostHref(episode.post_id);
    return {
      type: "navigate",
      label: postHref ? "View Free Preview Post" : "Browse Free Previews",
      href: postHref || "/library?tier=free",
      reason: postHref
        ? "Free preview pages link to the safe canonical Patreon post."
        : "No safe Patreon post URL is available; keep the user in free previews."
    };
  }

  if (eligible) {
    return {
      type: "resolve",
      label: "Open Full Episode",
      reason: "Eligible member; resolve the private destination server-side."
    };
  }

  if (accessLevel === "visitor") {
    return {
      type: "navigate",
      label: unlockLabel(episode),
      href: PATREON_JOIN_URL,
      reason: "Visitor needs a Patreon membership before private access can be checked."
    };
  }

  return {
    type: "navigate",
    label: upgradeLabel(episode),
    href: PATREON_JOIN_URL,
    reason: "Authenticated account does not currently meet the effective access tier."
  };
}

export function getSecondaryAccessAction(episode: Episode, accessLevel: AccessLevel) {
  const effective = getEffectiveEpisodeAccess(episode);
  if (canAccessTier(effective.effectiveTierRequired, accessLevel)) {
    return { label: "Preview details", href: `/episode/${episode.slug}` };
  }
  if (accessLevel === "visitor") {
    return { label: "Membership options", href: `/episode/${episode.slug}` };
  }
  if (effective.isProgressiveCut || effective.isAllPaidMembers) {
    return { label: "Membership options", href: `/episode/${episode.slug}` };
  }
  if (effective.isPlatinumEarlyAccess) {
    return { label: "Membership options", href: `/episode/${episode.slug}` };
  }
  return { label: "Account", href: "/account" };
}
